// frontend/src/pages/ProfileEdit.jsx
// アカウント設定【2026-09-14 サイドバー方式に作り替え】
//
// 節ごとに分けた理由:
//   設定が縦に積み上がると、下にあるものは存在を知られないまま埋もれる。
//   実際、メール変更・通知設定・自動出席・退会はサーバー側だけ実装されていて
//   画面から一度も触れなかった。節に分けて左に並べれば「ある」ことが分かる。
//
// 直したバグ（どちらも押しても必ず失敗していた）:
//   1. プロフィール保存が email を送っていた
//      → サーバーは email キーがあるだけで 400（乗っ取り経路を塞ぐため 2026-05-27）。
//        そもそも GET /api/user/me は email を返さないので、欄は常に空だった。
//        メール変更は専用の節（確認コード付き）に移した。
//   2. パスワード変更が PUT /api/user/password を叩いていた
//      → このエンドポイントは 410（廃止済み）。
//        change-request → change-verify の2段階フローに直した。
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import useDashboardSettings, { DASHBOARD_LAYOUTS } from '../hooks/useDashboardSettings';

// 節の定義
const SECTIONS = [
  { key: 'profile',  label: 'プロフィール',   desc: '氏名・学籍番号' },
  { key: 'password', label: 'パスワード',     desc: '確認コードで変更' },
  { key: 'email',    label: 'メールアドレス', desc: '確認コードで変更' },
  { key: 'notify',   label: '通知',           desc: '受け取る通知の種類' },
  { key: 'device',   label: '自動出席・端末', desc: '自動出席と信頼端末' },
  { key: 'display',  label: '画面の表示',     desc: 'ダッシュボードの並び' },
  { key: 'danger',   label: '退会',           desc: 'アカウントの削除', danger: true },
];


const NOTIFY_ITEMS = [
  { key: 'push_enabled',         label: 'アプリのプッシュ通知', hint: '端末に通知を出す' },
  { key: 'email_enabled',        label: 'メール通知',           hint: '登録メールアドレスに送る' },
  { key: 'attendance_reminder',  label: '出席リマインダー',     hint: '授業の前に知らせる' },
  { key: 'low_attendance_alert', label: '出席率の低下',         hint: '出席率が下がったときに知らせる' },
  { key: 'class_cancellation',   label: '休講・変更',           hint: '休講や時間変更を知らせる' },
  { key: 'custom_message',       label: '教員からのお知らせ',   hint: '個別に送られるメッセージ' },
];

const ProfileEdit = ({ user, onUserUpdate }) => {
  const [section, setSection] = useState('profile');
  const [dash, updateDash, dashStored] = useDashboardSettings();
  const [loading, setLoading] = useState(true);

  // プロフィール
  const [form, setForm]       = useState({ name: '', student_id: '' });
  const [saving, setSaving]   = useState(false);
  const [message, setMessage] = useState(null); // { type, text }

  // パスワード（2段階: コード要求 → コード+新パスワード）
  const [pwStep, setPwStep] = useState(1);
  const [pwForm, setPwForm] = useState({
    currentPassword: '', code: '', newPassword: '', confirmPassword: '',
  });
  const [savingPw, setSavingPw]   = useState(false);
  const [pwMessage, setPwMessage] = useState(null);

  // メールアドレス（2段階: 新アドレスへコード送信 → コード確認）
  const [mailStep, setMailStep] = useState(1);
  const [mailForm, setMailForm] = useState({ newEmail: '', code: '' });
  const [savingMail, setSavingMail]   = useState(false);
  const [mailMessage, setMailMessage] = useState(null);

  // 通知設定
  const [notify, setNotify] = useState(null);
  const [savingNotify, setSavingNotify]   = useState(false);
  const [notifyMessage, setNotifyMessage] = useState(null);

  // 自動出席・信頼端末
  const [autoAttendance, setAutoAttendance] = useState(null);
  const [deviceMessage, setDeviceMessage]   = useState(null);

  // 退会
  const [delPassword, setDelPassword] = useState('');
  const [delConfirm, setDelConfirm]   = useState('');
  const [deleting, setDeleting]       = useState(false);
  const [delMessage, setDelMessage]   = useState(null);

  const isStudent = user?.role === 'student';
  const isTeacher = user?.role === 'teacher' || user?.role === 'developer';

  const auth     = () => ({ Authorization: 'Bearer ' + localStorage.getItem('token') });
  const authJson = () => ({ ...auth(), 'Content-Type': 'application/json' });

  useEffect(() => { fetchProfile(); }, []);

  // 節を開いたときに必要なものだけ取りに行く（設定画面を開くたび全部叩かない）
  useEffect(() => {
    if (section === 'notify' && notify === null) fetchNotify();
    if (section === 'device' && autoAttendance === null && isTeacher) fetchAutoAttendance();
  }, [section]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProfile = async () => {
    try {
      const res  = await fetch('/api/user/me', { headers: auth() });
      const data = await res.json();
      if (data.success) {
        // メールアドレスは API が返さない（個人情報露出最小化方針）。
        // ここに欄を作らないのはそのため。変更は「メールアドレス」の節から。
        setForm({
          name:       data.user.name       || '',
          student_id: data.user.student_id || '',
        });
      }
    } catch (e) {
      console.error('プロフィール取得エラー:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotify = async () => {
    try {
      const res  = await fetch('/api/notifications/settings', { headers: auth() });
      const data = await res.json();
      if (data.success) setNotify(data.settings);
    } catch (e) {
      setNotifyMessage({ type: 'error', text: '通知設定の取得に失敗しました' });
    }
  };

  const fetchAutoAttendance = async () => {
    try {
      const res  = await fetch('/api/user/auto-attendance', { headers: auth() });
      const data = await res.json();
      if (data.success) setAutoAttendance(!!data.enabled);
    } catch (e) {
      setDeviceMessage({ type: 'error', text: '自動出席の設定を取得できませんでした' });
    }
  };

  // ── プロフィール保存 ──────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setMessage(null);

    // email は絶対に入れない。キーがあるだけでサーバーが 400 を返す。
    const body = { name: form.name };
    if (isStudent) body.student_id = form.student_id;

    try {
      const res  = await fetch('/api/user/me', {
        method: 'PUT', headers: authJson(), body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        setMessage({ type: 'success', text: 'プロフィールを更新しました' });
        if (onUserUpdate) onUserUpdate(data.user);
      } else {
        setMessage({
          type: 'error',
          text: data.code === 'STUDENT_ID_TAKEN'
            ? 'その学籍番号はすでに使用されています'
            : (data.message || '更新に失敗しました'),
        });
      }
    } catch (e) {
      setMessage({ type: 'error', text: 'ネットワークエラーが発生しました' });
    } finally {
      setSaving(false);
    }
  };

  // ── パスワード変更 ────────────────────────────────────────
  //   1. 現在のパスワードを送る → 登録メールアドレスに6桁コード
  //   2. コード + 新しいパスワード → 確定。新しいトークンが返るので必ず差し替える
  //      （パスワード変更で古いトークンは失効する。差し替えないと即ログアウトになる）
  const requestPasswordCode = async (e) => {
    e.preventDefault();
    setSavingPw(true); setPwMessage(null);
    try {
      const res  = await fetch('/api/user/password/change-request', {
        method: 'POST', headers: authJson(),
        body: JSON.stringify({ currentPassword: pwForm.currentPassword }),
      });
      const data = await res.json();
      if (data.success) {
        setPwStep(2);
        setPwMessage({ type: 'success', text: '登録メールアドレスに確認コードを送りました' });
      } else {
        setPwMessage({
          type: 'error',
          text: data.code === 'CURRENT_PASSWORD_INVALID'
            ? '現在のパスワードが正しくありません'
            : (data.message || 'コードの送信に失敗しました'),
        });
      }
    } catch (e) {
      setPwMessage({ type: 'error', text: 'ネットワークエラーが発生しました' });
    } finally {
      setSavingPw(false);
    }
  };

  const verifyPasswordChange = async (e) => {
    e.preventDefault();
    if (pwForm.newPassword !== pwForm.confirmPassword) {
      setPwMessage({ type: 'error', text: '新しいパスワードが一致しません' });
      return;
    }
    if (pwForm.newPassword.length < 8) {
      setPwMessage({ type: 'error', text: 'パスワードは8文字以上にしてください' });
      return;
    }
    setSavingPw(true); setPwMessage(null);
    try {
      const res  = await fetch('/api/user/password/change-verify', {
        method: 'POST', headers: authJson(),
        body: JSON.stringify({ code: pwForm.code, newPassword: pwForm.newPassword }),
      });
      const data = await res.json();
      if (data.success) {
        // 古いトークンは失効済み。ここで差し替えないと次のリクエストで落ちる。
        if (data.token) localStorage.setItem('token', data.token);
        setPwStep(1);
        setPwForm({ currentPassword: '', code: '', newPassword: '', confirmPassword: '' });
        setPwMessage({ type: 'success', text: 'パスワードを変更しました' });
      } else {
        setPwMessage({ type: 'error', text: data.message || '変更に失敗しました' });
      }
    } catch (e) {
      setPwMessage({ type: 'error', text: 'ネットワークエラーが発生しました' });
    } finally {
      setSavingPw(false);
    }
  };

  // ── メールアドレス変更 ────────────────────────────────────
  //   確認コードは「新しいアドレス」宛に送られる。
  //   受け取れないアドレスには変更できない、という作りになっている。
  const requestEmailCode = async (e) => {
    e.preventDefault();
    setSavingMail(true); setMailMessage(null);
    try {
      const res  = await fetch('/api/user/change-email', {
        method: 'POST', headers: authJson(),
        body: JSON.stringify({ newEmail: mailForm.newEmail }),
      });
      const data = await res.json();
      if (data.success) {
        setMailStep(2);
        setMailMessage({ type: 'success', text: '新しいメールアドレスに確認コードを送りました' });
      } else {
        setMailMessage({
          type: 'error',
          text: data.code === 'CHANGE_EMAIL_COOLDOWN'
            ? `続けて送れません。${data.retryAfterSeconds || 60}秒ほど待ってください`
            : (data.message || '送信に失敗しました'),
        });
      }
    } catch (e) {
      setMailMessage({ type: 'error', text: 'ネットワークエラーが発生しました' });
    } finally {
      setSavingMail(false);
    }
  };

  const verifyEmailChange = async (e) => {
    e.preventDefault();
    setSavingMail(true); setMailMessage(null);
    try {
      const res  = await fetch('/api/user/change-email/verify', {
        method: 'POST', headers: authJson(),
        body: JSON.stringify({ code: mailForm.code }),
      });
      const data = await res.json();
      if (data.success) {
        setMailStep(1);
        setMailForm({ newEmail: '', code: '' });
        setMailMessage({ type: 'success', text: 'メールアドレスを変更しました' });
      } else {
        setMailMessage({ type: 'error', text: data.message || '確認に失敗しました' });
      }
    } catch (e) {
      setMailMessage({ type: 'error', text: 'ネットワークエラーが発生しました' });
    } finally {
      setSavingMail(false);
    }
  };

  // ── 通知設定 ──────────────────────────────────────────────
  const saveNotify = async (next) => {
    setSavingNotify(true); setNotifyMessage(null);
    try {
      const res  = await fetch('/api/notifications/settings', {
        method: 'PUT', headers: authJson(), body: JSON.stringify(next),
      });
      const data = await res.json();
      if (data.success) {
        setNotify(data.settings);
        setNotifyMessage({ type: 'success', text: '通知設定を保存しました' });
      } else {
        setNotifyMessage({ type: 'error', text: data.message || '保存に失敗しました' });
      }
    } catch (e) {
      setNotifyMessage({ type: 'error', text: 'ネットワークエラーが発生しました' });
    } finally {
      setSavingNotify(false);
    }
  };

  const toggleNotify = (key) => {
    if (!notify) return;
    // サーバーは「送られてこなかった項目は true」として扱うので、
    // 一部だけ送ると触っていない設定が勝手に戻る。必ず全項目を送る。
    const next = {};
    for (const item of NOTIFY_ITEMS) next[item.key] = notify[item.key] !== false;
    next[key] = !(notify[key] !== false);
    setNotify({ ...notify, ...next });   // 先に画面へ反映（待たせない）
    saveNotify(next);
  };

  // ── 自動出席・信頼端末 ────────────────────────────────────
  const toggleAutoAttendance = async () => {
    const next = !autoAttendance;
    setAutoAttendance(next);
    setDeviceMessage(null);
    try {
      const res  = await fetch('/api/user/auto-attendance', {
        method: 'PUT', headers: authJson(), body: JSON.stringify({ enabled: next }),
      });
      const data = await res.json();
      if (data.success) {
        setDeviceMessage({ type: 'success', text: data.message });
      } else {
        setAutoAttendance(!next);
        setDeviceMessage({ type: 'error', text: data.message || '変更に失敗しました' });
      }
    } catch (e) {
      setAutoAttendance(!next);
      setDeviceMessage({ type: 'error', text: 'ネットワークエラーが発生しました' });
    }
  };

  const revokeTrustedDevices = async () => {
    if (!window.confirm('この端末を含め、信頼済みの端末をすべて解除します。よろしいですか？')) return;
    setDeviceMessage(null);
    try {
      const res = await fetch('/api/auth/login-mfa/trusted-devices', {
        method: 'DELETE', headers: auth(),
      });
      // MFA_LOGIN_ENABLED が true でないサーバーでは 404 が返る（機能が眠っている）
      if (res.status === 404) {
        setDeviceMessage({ type: 'error', text: 'この環境では2段階認証を使っていないため、信頼端末はありません' });
        return;
      }
      const data = await res.json();
      setDeviceMessage(data.success
        ? { type: 'success', text: data.message || '信頼端末を解除しました' }
        : { type: 'error', text: data.error || data.message || '解除に失敗しました' });
    } catch (e) {
      setDeviceMessage({ type: 'error', text: 'ネットワークエラーが発生しました' });
    }
  };

  // ── 退会 ──────────────────────────────────────────────────
  const deleteAccount = async (e) => {
    e.preventDefault();
    if (delConfirm !== '退会します') {
      setDelMessage({ type: 'error', text: '確認のため「退会します」と入力してください' });
      return;
    }
    if (!window.confirm('アカウントを削除します。この操作は取り消せません。よろしいですか？')) return;
    setDeleting(true); setDelMessage(null);
    try {
      const res  = await fetch('/api/user/me', {
        method: 'DELETE', headers: authJson(),
        body: JSON.stringify({ password: delPassword }),
      });
      const data = await res.json();
      if (data.success) {
        localStorage.removeItem('token');
        window.location.href = '/';
      } else {
        setDelMessage({
          type: 'error',
          text: data.code === 'INVALID_PASSWORD'
            ? 'パスワードが正しくありません'
            : (data.message || '退会に失敗しました'),
        });
      }
    } catch (e) {
      setDelMessage({ type: 'error', text: 'ネットワークエラーが発生しました' });
    } finally {
      setDeleting(false);
    }
  };

  // ── スタイル定数 ──────────────────────────────────────────
  const styles = {
    container: { maxWidth: '900px', margin: '0 auto', padding: '2rem 1rem' },
    // 画面が狭いときは flexWrap で上下に積まれる
    layout: { display: 'flex', gap: '1.5rem', alignItems: 'flex-start', flexWrap: 'wrap' },
    nav: { flex: '1 1 200px', minWidth: '200px', maxWidth: '260px' },
    navItem: (active, danger) => ({
      display: 'block', width: '100%', textAlign: 'left',
      padding: '0.7rem 0.9rem', marginBottom: '0.35rem',
      border: 'none', borderRadius: '8px', cursor: 'pointer',
      background: active ? (danger ? '#fdecea' : '#e3f2fd') : 'transparent',
      color: danger ? '#c62828' : (active ? '#1565c0' : '#374151'),
      fontSize: '0.95rem', fontWeight: active ? 700 : 500,
      transition: 'background 0.15s',
    }),
    navDesc: { display: 'block', fontSize: '0.74rem', color: '#9ca3af', marginTop: '0.15rem', fontWeight: 400 },
    panel: { flex: '3 1 360px', minWidth: '280px' },
    card: {
      background: '#ffffff', borderRadius: '12px',
      boxShadow: '0 2px 12px rgba(0,0,0,0.08)', padding: '2rem', marginBottom: '1.5rem',
    },
    sectionTitle: {
      fontSize: '1.1rem', fontWeight: '600', color: '#2c3e50',
      marginBottom: '1.5rem', paddingBottom: '0.75rem',
      borderBottom: '2px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: '0.5rem',
    },
    fieldGroup: { marginBottom: '1.25rem' },
    label: { display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#555', marginBottom: '0.4rem' },
    input: {
      width: '100%', padding: '0.65rem 0.9rem', border: '1.5px solid #ddd',
      borderRadius: '8px', fontSize: '1rem', color: '#2c3e50',
      boxSizing: 'border-box', transition: 'border-color 0.2s', outline: 'none',
    },
    badge: (role) => ({
      display: 'inline-block', padding: '0.2rem 0.7rem', borderRadius: '20px',
      fontSize: '0.78rem', fontWeight: '600',
      background: role === 'student' ? '#e8f5e9' :
                  role === 'teacher' ? '#e3f2fd' :
                  role === 'academic_staff' ? '#fff3e0' : '#f3e5f5',
      color:      role === 'student' ? '#2e7d32' :
                  role === 'teacher' ? '#1565c0' :
                  role === 'academic_staff' ? '#e65100' : '#6a1b9a',
    }),
    roleLabel: { student: '学生', teacher: '教師', academic_staff: '教務', developer: '開発者' },
    btn: (color = '#3498db', disabled = false) => ({
      width: '100%', padding: '0.75rem',
      background: disabled ? '#ccc' : color, color: '#fff',
      border: 'none', borderRadius: '8px', fontSize: '1rem', fontWeight: '600',
      cursor: disabled ? 'not-allowed' : 'pointer', marginTop: '0.5rem', transition: 'background 0.2s',
    }),
    alert: (type) => ({
      padding: '0.75rem 1rem', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.9rem',
      background: type === 'success' ? '#e8f5e9' : '#fdecea',
      color:      type === 'success' ? '#2e7d32' : '#c62828',
      border: `1px solid ${type === 'success' ? '#a5d6a7' : '#ef9a9a'}`,
    }),
    hint: { fontSize: '0.8rem', color: '#999', marginTop: '0.3rem' },
    toggleRow: {
      display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
      padding: '0.8rem 0', borderBottom: '1px solid #f3f4f6', cursor: 'pointer',
    },
    stepNote: {
      fontSize: '0.82rem', color: '#6b7280', background: '#f9fafb',
      borderRadius: '8px', padding: '0.6rem 0.8rem', marginBottom: '1rem', lineHeight: 1.7,
    },
  };

  const focusOn  = e => { e.target.style.borderColor = '#3498db'; };
  const focusOff = e => { e.target.style.borderColor = '#ddd'; };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: '3rem', color: '#999' }}>読み込み中...</div>
      </div>
    );
  }

  const current = SECTIONS.find(s => s.key === section) || SECTIONS[0];

  return (
    <div style={styles.container}>

      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '1.6rem', fontWeight: '700', color: '#2c3e50', marginBottom: '0.5rem' }}>
          アカウント設定
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ color: '#888', fontSize: '0.9rem' }}>ロール:</span>
          <span style={styles.badge(user?.role)}>
            {styles.roleLabel[user?.role] || user?.role}
          </span>
        </div>
      </div>

      <div style={styles.layout}>

        {/* 左: 設定の目次 */}
        <nav style={styles.nav}>
          {SECTIONS.map(s => (
            <button
              key={s.key}
              type="button"
              onClick={() => setSection(s.key)}
              style={styles.navItem(section === s.key, s.danger)}
            >
              {s.label}
              <span style={styles.navDesc}>{s.desc}</span>
            </button>
          ))}
          {/* 普段は使わない画面の入口【2026-09-17】（サイドバーから移した） */}
          {['teacher', 'academic_staff', 'developer'].includes(user?.role) && (
            <>
              <div style={{ fontSize: '0.72rem', color: '#9ca3af', margin: '0.9rem 0 0.2rem 0.6rem' }}>その他</div>
              <Link to="/users-audit" style={{ ...styles.navItem(false, false), textDecoration: 'none', display: 'block' }}>
                変更履歴
                <span style={styles.navDesc}>氏名・学籍番号などの修正記録</span>
              </Link>
            </>
          )}
        </nav>

        {/* 右: 選んだ節 */}
        <div style={styles.panel}>
          <div style={styles.card}>
            <div style={styles.sectionTitle}>{current.label}</div>

            {/* ── プロフィール ── */}
            {section === 'profile' && (
              <>
                {message && <div style={styles.alert(message.type)}>{message.text}</div>}
                <form onSubmit={handleSubmit}>
                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>氏名</label>
                    <input
                      style={styles.input} type="text" value={form.name}
                      onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                      placeholder="山田 太郎" required
                      onFocus={focusOn} onBlur={focusOff}
                    />
                  </div>

                  {isStudent && (
                    <div style={styles.fieldGroup}>
                      <label style={styles.label}>学籍番号</label>
                      <input
                        style={styles.input} type="text" value={form.student_id}
                        onChange={e => setForm(p => ({ ...p, student_id: e.target.value }))}
                        placeholder="S2025001" required
                        onFocus={focusOn} onBlur={focusOff}
                      />
                      <p style={styles.hint}>入力した学籍番号でログインID・出席記録が管理されます</p>
                    </div>
                  )}

                  <p style={styles.hint}>
                    メールアドレスの変更は左の「メールアドレス」から行えます。
                  </p>

                  <button type="submit" disabled={saving} style={styles.btn('#3498db', saving)}>
                    {saving ? '保存中...' : '変更を保存'}
                  </button>
                </form>
              </>
            )}

            {/* ── パスワード ── */}
            {section === 'password' && (
              <>
                {pwMessage && <div style={styles.alert(pwMessage.type)}>{pwMessage.text}</div>}
                <div style={styles.stepNote}>
                  パスワードの変更には確認コードが必要です。
                  現在のパスワードを入れると、<strong>登録しているメールアドレス</strong>に6桁のコードが届きます。
                </div>

                {pwStep === 1 ? (
                  <form onSubmit={requestPasswordCode}>
                    <div style={styles.fieldGroup}>
                      <label style={styles.label}>現在のパスワード</label>
                      <input
                        style={styles.input} type="password" value={pwForm.currentPassword}
                        onChange={e => setPwForm(p => ({ ...p, currentPassword: e.target.value }))}
                        required onFocus={focusOn} onBlur={focusOff}
                      />
                    </div>
                    <button type="submit" disabled={savingPw} style={styles.btn('#3498db', savingPw)}>
                      {savingPw ? '送信中...' : '確認コードを送る'}
                    </button>
                  </form>
                ) : (
                  <form onSubmit={verifyPasswordChange}>
                    <div style={styles.fieldGroup}>
                      <label style={styles.label}>確認コード（6桁）</label>
                      <input
                        style={styles.input} type="text" inputMode="numeric" maxLength={6}
                        value={pwForm.code}
                        onChange={e => setPwForm(p => ({ ...p, code: e.target.value }))}
                        placeholder="123456" required onFocus={focusOn} onBlur={focusOff}
                      />
                    </div>
                    <div style={styles.fieldGroup}>
                      <label style={styles.label}>新しいパスワード</label>
                      <input
                        style={styles.input} type="password" value={pwForm.newPassword}
                        onChange={e => setPwForm(p => ({ ...p, newPassword: e.target.value }))}
                        placeholder="8文字以上" required onFocus={focusOn} onBlur={focusOff}
                      />
                    </div>
                    <div style={styles.fieldGroup}>
                      <label style={styles.label}>新しいパスワード（確認）</label>
                      <input
                        style={styles.input} type="password" value={pwForm.confirmPassword}
                        onChange={e => setPwForm(p => ({ ...p, confirmPassword: e.target.value }))}
                        required onFocus={focusOn} onBlur={focusOff}
                      />
                    </div>
                    <button type="submit" disabled={savingPw} style={styles.btn('#e74c3c', savingPw)}>
                      {savingPw ? '変更中...' : 'パスワードを変更'}
                    </button>
                    <button type="button" onClick={() => { setPwStep(1); setPwMessage(null); }}
                            style={{ ...styles.btn('#9ca3af', false), marginTop: '0.5rem' }}>
                      やり直す
                    </button>
                  </form>
                )}
              </>
            )}

            {/* ── メールアドレス ── */}
            {section === 'email' && (
              <>
                {mailMessage && <div style={styles.alert(mailMessage.type)}>{mailMessage.text}</div>}
                <div style={styles.stepNote}>
                  確認コードは<strong>新しいメールアドレス宛</strong>に送られます。
                  受け取れないアドレスには変更できません。
                  現在のアドレスは、安全のため画面には表示していません。
                </div>

                {mailStep === 1 ? (
                  <form onSubmit={requestEmailCode}>
                    <div style={styles.fieldGroup}>
                      <label style={styles.label}>新しいメールアドレス</label>
                      <input
                        style={styles.input} type="email" value={mailForm.newEmail}
                        onChange={e => setMailForm(p => ({ ...p, newEmail: e.target.value }))}
                        placeholder="example@university.ac.jp" required
                        onFocus={focusOn} onBlur={focusOff}
                      />
                    </div>
                    <button type="submit" disabled={savingMail} style={styles.btn('#3498db', savingMail)}>
                      {savingMail ? '送信中...' : '確認コードを送る'}
                    </button>
                  </form>
                ) : (
                  <form onSubmit={verifyEmailChange}>
                    <div style={styles.fieldGroup}>
                      <label style={styles.label}>確認コード（6桁）</label>
                      <input
                        style={styles.input} type="text" inputMode="numeric" maxLength={6}
                        value={mailForm.code}
                        onChange={e => setMailForm(p => ({ ...p, code: e.target.value }))}
                        placeholder="123456" required onFocus={focusOn} onBlur={focusOff}
                      />
                      <p style={styles.hint}>{mailForm.newEmail} に送信しました</p>
                    </div>
                    <button type="submit" disabled={savingMail} style={styles.btn('#3498db', savingMail)}>
                      {savingMail ? '確認中...' : 'メールアドレスを変更'}
                    </button>
                    <button type="button" onClick={() => { setMailStep(1); setMailMessage(null); }}
                            style={{ ...styles.btn('#9ca3af', false), marginTop: '0.5rem' }}>
                      やり直す
                    </button>
                  </form>
                )}
              </>
            )}

            {/* ── 通知 ── */}
            {section === 'notify' && (
              <>
                {notifyMessage && <div style={styles.alert(notifyMessage.type)}>{notifyMessage.text}</div>}
                {notify === null ? (
                  <p style={{ color: '#999' }}>読み込み中...</p>
                ) : (
                  <div>
                    {NOTIFY_ITEMS.map(item => (
                      <label key={item.key} style={styles.toggleRow}>
                        <input
                          type="checkbox"
                          checked={notify[item.key] !== false}
                          disabled={savingNotify}
                          onChange={() => toggleNotify(item.key)}
                          style={{ marginTop: '0.2rem' }}
                        />
                        <span>
                          <span style={{ fontWeight: 600, color: '#374151' }}>{item.label}</span>
                          <span style={styles.navDesc}>{item.hint}</span>
                        </span>
                      </label>
                    ))}
                    <p style={styles.hint}>切り替えるとすぐ保存されます。</p>
                  </div>
                )}
              </>
            )}

            {/* ── 自動出席・端末 ── */}
            {section === 'device' && (
              <>
                {deviceMessage && <div style={styles.alert(deviceMessage.type)}>{deviceMessage.text}</div>}

                {isTeacher && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={styles.toggleRow}>
                      <input
                        type="checkbox"
                        checked={autoAttendance === true}
                        disabled={autoAttendance === null}
                        onChange={toggleAutoAttendance}
                        style={{ marginTop: '0.2rem' }}
                      />
                      <span>
                        <span style={{ fontWeight: 600, color: '#374151' }}>自動出席を使う</span>
                        <span style={styles.navDesc}>
                          時間割に沿って出席受付を自動で開始します。
                          実際に動くのは、システム全体の設定とスケジュール側の設定も有効なときだけです。
                        </span>
                      </span>
                    </label>
                  </div>
                )}

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>信頼済みの端末</label>
                  <p style={styles.hint}>
                    2段階認証を省略できる端末の登録をすべて取り消します。
                    端末をなくしたときや、共用のパソコンで使ってしまったときに押してください。
                  </p>
                  <button type="button" onClick={revokeTrustedDevices} style={styles.btn('#e67e22', false)}>
                    信頼端末をすべて解除
                  </button>
                </div>
              </>
            )}

            {/* ── 退会 ── */}
            {/* ── 画面の表示 ── */}
            {section === 'display' && (
              <div>
                <p style={styles.stepNote}>
                  最初に出るダッシュボードの並べ方を選べます。
                  選ぶとすぐ保存され、別のパソコンやスマホで開いても同じ並びになります。
                </p>

                {DASHBOARD_LAYOUTS.map(opt => (
                  <label key={opt.key} style={styles.toggleRow}>
                    <input
                      type="radio"
                      name="dashboard-layout"
                      checked={dash.layout === opt.key}
                      onChange={() => updateDash({ layout: opt.key })}
                      style={{ marginTop: '0.25rem' }}
                    />
                    <span>
                      <span style={{ fontWeight: 600, color: '#374151' }}>{opt.label}</span>
                      <span style={styles.navDesc}>{opt.desc}</span>
                    </span>
                  </label>
                ))}

                {dashStored === false && (
                  <p style={styles.hint}>
                    サーバーに保存できないため、この設定はこのブラウザにだけ残ります。
                  </p>
                )}
              </div>
            )}

            {section === 'danger' && (
              <>
                {delMessage && <div style={styles.alert(delMessage.type)}>{delMessage.text}</div>}
                <div style={{ ...styles.stepNote, background: '#fdecea', color: '#c62828' }}>
                  アカウントを削除すると、氏名・メールアドレスなどの個人を特定できる情報は消えます。
                  <strong>この操作は取り消せません。</strong>
                </div>
                <form onSubmit={deleteAccount}>
                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>現在のパスワード</label>
                    <input
                      style={styles.input} type="password" value={delPassword}
                      onChange={e => setDelPassword(e.target.value)}
                      required onFocus={focusOn} onBlur={focusOff}
                    />
                  </div>
                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>確認のため「退会します」と入力してください</label>
                    <input
                      style={styles.input} type="text" value={delConfirm}
                      onChange={e => setDelConfirm(e.target.value)}
                      placeholder="退会します" required onFocus={focusOn} onBlur={focusOff}
                    />
                  </div>
                  <button type="submit" disabled={deleting} style={styles.btn('#c62828', deleting)}>
                    {deleting ? '処理中...' : 'アカウントを削除する'}
                  </button>
                </form>
              </>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileEdit;
